/**
 * Hero CTA Summary — tarjeta en el AdminDashboard.
 *
 * El hero CTA del home no es UN único valor: depende del tipo de visitante.
 * Esta tarjeta describe con precisión QUÉ se renderiza en cada segmento y
 * apunta al admin que controla cada palanca. Es la única referencia
 * autoritativa del comportamiento del hero — no leer otras pantallas para esto.
 *
 *  ARGENTINA (AR / UNKNOWN+anaafernandez.com)  →  BaseTemplate (galería)
 *    Hero CTA = botón sobre el header (community / birthday / wishlist / WhatsApp).
 *    Controles:
 *      • Toggle global: /admin/wishlist-config  (`hideGlobalHeroCta`)
 *      • Override per geo: /admin/cta            (categories: telegram_basic, vip_membership, free_channel, whatsapp_broadcast)
 *      • Worker flag: /admin/worker-flags        (`GLOBAL_HIDE_HERO_CTA` — Cloudflare env)
 *
 *  INTERNACIONAL (no-AR)  →  template Mux (InvitacionExclusivaMux por default)
 *    Hero CTA = `<MuxVideoPlayerWithCTA>` fullscreen + botón "Acceder a la membresía" (Stripe).
 *    Controles:
 *      • Plantilla por país: /admin/country-templates
 *      • Video Mux por país: /admin/country-templates
 *      • Video Mux global (fallback): /admin/premium-invite-config
 *      • Stripe Price ID: /admin/suscripciones (membership_products, plan_tier=vip).
 *        El admin lo crea en Stripe Dashboard y lo pega ahí — sin env vars.
 *      • Post-pago: /portal/completar-perfil → /pending-approval → /mi-acceso
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { GeoSimCountryPreviewButton } from "@/components/admin/GeoSimCountryPreviewButton";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  ExternalLink,
  Eye,
  Film,
  Flag,
  Globe,
  Megaphone,
  PartyPopper,
  ShieldAlert,
  Sparkles,
  Target,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DEFAULT_INTERNATIONAL_HOME_TEMPLATE,
  isInternationalHomeMuxPlayerTemplate,
} from "@/lib/internationalHomeTemplate";

interface MembershipPriceStatus {
  status: "ok" | "warning" | "error";
  db: { configured: boolean; stripe_price_id: string | null; product_name: string | null };
  stripe: {
    price_active: boolean | null;
    currency: string | null;
    unit_amount_cents: number | null;
    interval: string | null;
    product_image: string | null;
    error: string | null;
  };
  logo: { env_value: string | null; in_sync: boolean | null };
  hints: string[];
}

interface MuxVideoStatus {
  status: "ok" | "warning" | "error";
  playback: {
    active: string | null;
    source: "db" | "env_es" | "env_en" | null;
    env: { es: string | null; en: string | null };
    db_overlay: string | null;
  };
  mux: {
    reachable: boolean | null;
    status: number | null;
    manifest_url: string | null;
    thumbnail_url: string | null;
    error: string | null;
  };
  hints: string[];
}

interface HeroState {
  /** Toggle global del Hero CTA (apaga ambos segmentos AR + Worker). */
  hideGlobalHeroCta: boolean;
  /** Plantilla internacional activa (idealmente Mux). */
  internationalHomeTemplate: string;
  /** Map override por país (admin) — si tiene entradas, ese país usa la plantilla específica. */
  internationalHomeTemplateByCountry: Record<string, string>;
  internationalHomeMuxByCountry: Record<string, string>;
  /** Playback ID del video Mux del hero internacional (categoría video.vip). */
  videoVipPlaybackId: string | null;
  /** Label del CTA principal (VIP membership). */
  vipMembershipLabel: string | null;
  /** Status live del Stripe Price ID (DB + Stripe API + logo sync). */
  priceStatus: MembershipPriceStatus | null;
  /** Status live del video Mux (playback ID + reachability + preview). */
  muxStatus: MuxVideoStatus | null;
  /** Loading / error */
  loading: boolean;
  error: string | null;
}

export function HeroCtaSummaryCard() {
  const { getAuthHeaders } = useAdminAuth();
  const [state, setState] = useState<HeroState>({
    hideGlobalHeroCta: false,
    internationalHomeTemplate: DEFAULT_INTERNATIONAL_HOME_TEMPLATE,
    internationalHomeTemplateByCountry: {},
    internationalHomeMuxByCountry: {},
    videoVipPlaybackId: null,
    vipMembershipLabel: null,
    priceStatus: null,
    muxStatus: null,
    loading: true,
    error: null,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const headers = await getAuthHeaders();
      const [wishlistRes, ctaStatusRes, siteSettingsRes, priceStatusRes, muxStatusRes] =
        await Promise.all([
          fetch("/api/admin/wishlist-config", { headers }).catch(() => null),
          fetch("/api/admin/cta-status", { headers }).catch(() => null),
          fetch("/api/admin/site-settings", { headers }).catch(() => null),
          fetch("/api/admin/membership-price-status", { headers }).catch(() => null),
          fetch("/api/admin/mux-video-status", { headers }).catch(() => null),
        ]);

      const wishlist = wishlistRes && wishlistRes.ok ? await wishlistRes.json().catch(() => ({})) : {};
      const ctaStatus = ctaStatusRes && ctaStatusRes.ok ? await ctaStatusRes.json().catch(() => ({})) : {};
      const siteSettings =
        siteSettingsRes && siteSettingsRes.ok ? await siteSettingsRes.json().catch(() => ({})) : {};
      const priceStatus: MembershipPriceStatus | null =
        priceStatusRes && priceStatusRes.ok
          ? await priceStatusRes.json().catch(() => null)
          : null;
      const muxStatus: MuxVideoStatus | null =
        muxStatusRes && muxStatusRes.ok ? await muxStatusRes.json().catch(() => null) : null;

      const ctas: Array<{ id: string; category: string; label?: string; value?: unknown }> = Array.isArray(
        ctaStatus?.ctas,
      )
        ? ctaStatus.ctas
        : [];
      const videoRow = ctas.find((c) => c.category === "video" && /vip/i.test(c.id));
      const vipRow = ctas.find((c) => c.category === "vip_membership");

      setState({
        hideGlobalHeroCta: Boolean(wishlist?.hideGlobalHeroCta),
        internationalHomeTemplate: String(
          siteSettings?.config?.internationalHomeTemplate ||
            siteSettings?.internationalHomeTemplate ||
            DEFAULT_INTERNATIONAL_HOME_TEMPLATE,
        ),
        internationalHomeTemplateByCountry:
          (siteSettings?.config?.internationalHomeTemplateByCountry as Record<string, string>) ||
          (siteSettings?.internationalHomeTemplateByCountry as Record<string, string>) ||
          {},
        internationalHomeMuxByCountry:
          (siteSettings?.config?.internationalHomeMuxByCountry as Record<string, string>) ||
          (siteSettings?.internationalHomeMuxByCountry as Record<string, string>) ||
          {},
        videoVipPlaybackId: typeof videoRow?.value === "string" ? (videoRow.value as string) : null,
        vipMembershipLabel: typeof vipRow?.label === "string" ? (vipRow.label as string) : null,
        priceStatus,
        muxStatus,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : "No se pudo cargar el estado del Hero CTA",
      }));
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    void load();
  }, [load]);

  const intlIsMux = isInternationalHomeMuxPlayerTemplate(state.internationalHomeTemplate);
  const overrideEntries = Object.entries(state.internationalHomeTemplateByCountry || {}).filter(
    ([k]) => /^[A-Z]{2}$/.test(k),
  );
  const muxOverrideEntries = Object.entries(state.internationalHomeMuxByCountry || {}).filter(
    ([k, v]) => {
      if (!/^[A-Z]{2}$/.test(k)) return false;
      if (typeof v === "string") return v.trim().length > 0;
      if (v && typeof v === "object" && Array.isArray((v as { variants?: unknown }).variants)) {
        return (v as { variants: unknown[] }).variants.length > 0;
      }
      return false;
    },
  );
  const overrideViolations = overrideEntries.filter(
    ([, v]) => !isInternationalHomeMuxPlayerTemplate(v),
  );

  return (
    <Card className="border-purple-500/20 bg-gradient-to-br from-purple-950/40 via-zinc-950 to-zinc-950">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-[15px]">
              <Target className="h-4 w-4 text-purple-400" strokeWidth={2} />
              Hero CTA — fuente única de verdad
            </CardTitle>
            <CardDescription className="mt-1 text-xs text-zinc-400">
              El hero CTA del <code className="rounded bg-white/5 px-1">/</code> no es un único valor. Cambia según
              quién entra: Argentina ve la galería con botón overlay; internacional ve el video Mux con CTA de
              membresía. Esta tarjeta resume cada segmento y dónde se edita cada palanca.
            </CardDescription>
          </div>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 text-xs text-zinc-400 hover:text-white"
          >
            <Link to="/admin/cta">
              Ver detalle
              <ArrowUpRight className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {state.error && (
          <div className="flex items-start gap-2 rounded-md border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{state.error}</span>
          </div>
        )}

        {/* Estado global */}
        <div
          className={cn(
            "flex items-start gap-3 rounded-lg border px-3 py-2.5",
            state.hideGlobalHeroCta
              ? "border-red-500/30 bg-red-500/5"
              : "border-emerald-500/20 bg-emerald-500/5",
          )}
        >
          {state.hideGlobalHeroCta ? (
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
          ) : (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
          )}
          <div className="flex-1">
            <p className="text-[12.5px] font-medium text-zinc-100">
              Toggle global Hero CTA: {state.hideGlobalHeroCta ? "OCULTO" : "VISIBLE"}
            </p>
            <p className="mt-0.5 text-[11px] text-zinc-400">
              <code className="rounded bg-white/5 px-1">site_settings.hide_global_hero_cta</code> +{" "}
              <code className="rounded bg-white/5 px-1">GLOBAL_HIDE_HERO_CTA</code> (Cloudflare). Si cualquiera está
              en true → ocultos ambos segmentos.
            </p>
            <div className="mt-1.5 flex gap-2">
              <Button asChild size="sm" variant="outline" className="h-6 px-2 text-[10.5px]">
                <Link to="/admin/wishlist-config">Editar toggle (admin)</Link>
              </Button>
              <Button asChild size="sm" variant="outline" className="h-6 px-2 text-[10.5px]">
                <Link to="/admin/worker-flags">Editar flag Worker</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Segmento AR */}
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.03] p-3">
          <div className="mb-2 flex items-center gap-2">
            <Flag className="h-3.5 w-3.5 text-amber-400" />
            <p className="text-[12.5px] font-semibold text-zinc-100">
              Argentina · BaseTemplate (galería)
            </p>
            <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-[10px] text-amber-300">
              hero overlay
            </Badge>
          </div>
          <p className="text-[11.5px] leading-relaxed text-zinc-400">
            El hero CTA es un botón sobre el header. Se elige entre <code>community</code>,{" "}
            <code>birthday</code>, <code>wishlist</code> o <code>whatsapp_broadcast</code> según overrides por geo
            (país / provincia / ciudad).
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <CategoryChip icon={Sparkles} label="Community" to="/admin/cta?cat=free_channel" />
            <CategoryChip icon={PartyPopper} label="Special offer" to="/admin/cta?cat=special_offer" />
            <CategoryChip icon={Megaphone} label="WhatsApp" to="/admin/cta?cat=whatsapp_broadcast" />
            <CategoryChip icon={Globe} label="Wishlist por geo" to="/admin/wishlist-config" />
          </div>
        </div>

        {/* Segmento Internacional */}
        <div
          className={cn(
            "rounded-lg border p-3",
            intlIsMux
              ? "border-violet-500/25 bg-violet-500/[0.03]"
              : "border-red-500/30 bg-red-500/5",
          )}
        >
          <div className="mb-2 flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-violet-300" />
            <p className="text-[12.5px] font-semibold text-zinc-100">
              Internacional (no-AR) · template{" "}
              <span className={intlIsMux ? "text-violet-200" : "text-red-300"}>
                {state.internationalHomeTemplate}
              </span>
            </p>
            {intlIsMux ? (
              <Badge variant="outline" className="border-violet-500/30 bg-violet-500/10 text-[10px] text-violet-200">
                Mux ✓
              </Badge>
            ) : (
              <Badge variant="outline" className="border-red-500/30 bg-red-500/10 text-[10px] text-red-300">
                NO es Mux — conversión cae
              </Badge>
            )}
          </div>
          <p className="text-[11.5px] leading-relaxed text-zinc-400">
            El hero CTA es el botón sobre el <code>&lt;MuxVideoPlayerWithCTA&gt;</code> → suscripción Stripe
            “Membresía Exclusiva”. Post-pago va a <code>/portal/completar-perfil</code>.
          </p>

          {/* Video Mux — el componente principal de conversión */}
          {state.muxStatus ? (
            <MuxStatusBlock status={state.muxStatus} />
          ) : (
            state.videoVipPlaybackId && (
              <div className="mt-2 flex items-center gap-1.5 text-[10.5px] text-zinc-300">
                <Film className="h-3 w-3 text-violet-300" />
                <span className="text-zinc-400">Playback ID VIP:</span>
                <code className="rounded bg-white/5 px-1 font-mono text-[10px] text-zinc-200">
                  {state.videoVipPlaybackId.slice(0, 16)}
                  {state.videoVipPlaybackId.length > 16 ? "…" : ""}
                </code>
              </div>
            )
          )}
          {state.vipMembershipLabel && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[10.5px] text-zinc-300">
              <Target className="h-3 w-3 text-violet-300" />
              <span className="text-zinc-400">Label CTA:</span>
              <span className="text-zinc-200">{state.vipMembershipLabel}</span>
            </div>
          )}

          {/* Stripe Price ID — status live (DB + Stripe API) */}
          {state.priceStatus && <PriceStatusBlock status={state.priceStatus} />}

          {muxOverrideEntries.length > 0 && (
            <div className="mt-2">
              <p className="text-[10.5px] uppercase tracking-wide text-zinc-500">
                Video Mux por país ({muxOverrideEntries.length})
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {muxOverrideEntries.map(([code, pid]) => (
                  <Badge
                    key={code}
                    variant="outline"
                    className="border-amber-500/30 bg-amber-500/10 text-[10px] text-amber-100"
                  >
                    {code} · {pid.slice(0, 12)}
                    {pid.length > 12 ? "…" : ""}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {overrideEntries.length > 0 && (
            <div className="mt-2">
              <p className="text-[10.5px] uppercase tracking-wide text-zinc-500">Overrides por país</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {overrideEntries.map(([code, tpl]) => {
                  const violation = overrideViolations.some(([c]) => c === code);
                  return (
                    <Badge
                      key={code}
                      variant="outline"
                      className={cn(
                        "text-[10px]",
                        violation
                          ? "border-red-500/40 bg-red-500/10 text-red-200"
                          : "border-violet-500/30 bg-violet-500/10 text-violet-100",
                      )}
                    >
                      {code} → {tpl}
                      {violation && " ✗"}
                    </Badge>
                  );
                })}
              </div>
              {overrideViolations.length > 0 && (
                <p className="mt-1 text-[10.5px] text-red-300">
                  ⚠ Hay países con template NO-Mux. Internacional debe usar siempre Mux para convertir.
                </p>
              )}
            </div>
          )}

          <div className="mt-2 flex flex-wrap gap-1.5">
            <CategoryChip icon={Globe} label="Home por país" to="/admin/country-templates" />
            <CategoryChip icon={Film} label="Video Mux global" to="/admin/premium-invite-config" />
            <CategoryChip icon={Target} label="VIP membership" to="/admin/cta?cat=vip_membership" />
            <GeoSimCountryPreviewButton
              country="US"
              state="CA"
              source="hero_cta_summary_intl"
              size="sm"
              className="h-6 border-violet-500/30 bg-violet-500/10 px-2 text-[10.5px] text-violet-100 hover:bg-violet-500/20"
              label={
                <>
                  <Eye className="mr-1 h-3 w-3" />
                  GeoSim internacional
                </>
              }
              showIcon={false}
              title="Visitante US real (edge GeoSim · DualCTA/Mux + Stripe)"
            />
            <GeoSimCountryPreviewButton
              country="AR"
              province="CABA"
              city="Buenos Aires"
              source="hero_cta_summary_ar"
              size="sm"
              className="h-6 border-amber-500/30 bg-amber-500/10 px-2 text-[10.5px] text-amber-100 hover:bg-amber-500/20"
              label={
                <>
                  <Eye className="mr-1 h-3 w-3" />
                  GeoSim Argentina
                </>
              }
              showIcon={false}
              title="Visitante AR CABA real (edge · Base + CTA por región)"
            />
          </div>
        </div>

        {state.loading && (
          <p className="text-[11px] text-zinc-500">Resolviendo estado actual del Hero CTA…</p>
        )}
      </CardContent>
    </Card>
  );
}

function MuxStatusBlock({ status }: { status: MuxVideoStatus }) {
  const accent =
    status.status === "ok"
      ? "border-emerald-500/25 bg-emerald-500/[0.05]"
      : status.status === "warning"
        ? "border-amber-500/30 bg-amber-500/5"
        : "border-red-500/30 bg-red-500/5";
  const Icon =
    status.status === "ok"
      ? CheckCircle2
      : status.status === "warning"
        ? AlertTriangle
        : ShieldAlert;
  const iconColor =
    status.status === "ok"
      ? "text-emerald-400"
      : status.status === "warning"
        ? "text-amber-400"
        : "text-red-400";
  const sourceLabel =
    status.playback.source === "db"
      ? "/admin/premium-invite-config"
      : status.playback.source === "env_es"
        ? "VITE_PREMIUM_MUX_ES_VIP"
        : status.playback.source === "env_en"
          ? "VITE_PREMIUM_MUX_EN_VIP"
          : "(ninguno)";

  return (
    <div className={cn("mt-2 rounded-md border px-2.5 py-2", accent)}>
      <div className="flex items-start gap-2">
        <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", iconColor)} />
        <div className="flex-1 space-y-1">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-zinc-100">
            <Film className="h-3 w-3 text-violet-300" />
            Video Mux — {status.status === "ok" ? "OK" : status.status === "warning" ? "atención" : "error"}
          </p>
          <div className="flex items-start gap-2">
            {status.mux.thumbnail_url && status.playback.active && (
              <a
                href={status.mux.thumbnail_url}
                target="_blank"
                rel="noopener noreferrer"
                title="Abrir thumbnail en Mux"
                className="block h-12 w-20 shrink-0 overflow-hidden rounded border border-white/10 bg-black/40"
              >
                <img
                  src={status.mux.thumbnail_url}
                  alt="Mux thumbnail"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </a>
            )}
            <div className="grid flex-1 gap-0.5 text-[10.5px] leading-snug text-zinc-300">
              <div className="flex items-center gap-1.5">
                <span className="text-zinc-400">Playback ID:</span>
                {status.playback.active ? (
                  <code className="rounded bg-white/5 px-1 font-mono text-[10px] text-zinc-100">
                    {status.playback.active.slice(0, 16)}
                    {status.playback.active.length > 16 ? "…" : ""}
                  </code>
                ) : (
                  <span className="text-red-300">✗ no configurado</span>
                )}
                <span className="text-zinc-500">· {sourceLabel}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-zinc-400">Mux CDN:</span>
                {status.mux.reachable === true ? (
                  <span className="text-emerald-300">reachable (HTTP 200)</span>
                ) : status.mux.reachable === false ? (
                  <span className="text-red-300">✗ {status.mux.error ?? "no reachable"}</span>
                ) : (
                  <span className="text-zinc-500">—</span>
                )}
              </div>
              {status.mux.manifest_url && (
                <div className="flex items-center gap-1.5">
                  <a
                    href={status.mux.manifest_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 rounded border border-violet-500/25 bg-violet-500/10 px-1.5 py-0.5 text-[10px] text-violet-100 hover:bg-violet-500/20"
                  >
                    Manifest HLS
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                  <Link
                    to="/admin/premium-invite-config"
                    className="inline-flex items-center gap-0.5 rounded border border-violet-500/25 bg-violet-500/10 px-1.5 py-0.5 text-[10px] text-violet-100 hover:bg-violet-500/20"
                  >
                    Editar en admin
                    <ArrowUpRight className="h-2.5 w-2.5" />
                  </Link>
                </div>
              )}
            </div>
          </div>
          {status.hints.length > 0 && (
            <ul className="mt-1 list-disc space-y-0.5 pl-3.5 text-[10px] text-zinc-400">
              {status.hints.slice(0, 3).map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function PriceStatusBlock({ status }: { status: MembershipPriceStatus }) {
  const accent =
    status.status === "ok"
      ? "border-emerald-500/25 bg-emerald-500/[0.05]"
      : status.status === "warning"
        ? "border-amber-500/30 bg-amber-500/5"
        : "border-red-500/30 bg-red-500/5";
  const Icon =
    status.status === "ok"
      ? CheckCircle2
      : status.status === "warning"
        ? AlertTriangle
        : ShieldAlert;
  const iconColor =
    status.status === "ok"
      ? "text-emerald-400"
      : status.status === "warning"
        ? "text-amber-400"
        : "text-red-400";
  const formattedPrice =
    typeof status.stripe.unit_amount_cents === "number" && status.stripe.currency
      ? `${(status.stripe.unit_amount_cents / 100).toFixed(2)} ${status.stripe.currency.toUpperCase()}`
      : null;

  return (
    <div className={cn("mt-2 rounded-md border px-2.5 py-2", accent)}>
      <div className="flex items-start gap-2">
        <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", iconColor)} />
        <div className="flex-1 space-y-1">
          <p className="text-[11px] font-semibold text-zinc-100">
            Stripe Price ID — {status.status === "ok" ? "OK" : status.status === "warning" ? "atención" : "error"}
          </p>
          <div className="grid gap-0.5 text-[10.5px] leading-snug text-zinc-300">
            <div className="flex items-center gap-1.5">
              <span className="text-zinc-400">DB:</span>
              {status.db.configured ? (
                <code className="rounded bg-white/5 px-1 font-mono text-[10px] text-zinc-100">
                  {status.db.stripe_price_id?.slice(0, 18)}
                  {status.db.stripe_price_id && status.db.stripe_price_id.length > 18 ? "…" : ""}
                </code>
              ) : (
                <span className="text-red-300">✗ no configurado</span>
              )}
              {status.db.product_name && (
                <span className="text-zinc-500">· {status.db.product_name}</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-zinc-400">Stripe live:</span>
              {status.stripe.error ? (
                <span className="text-red-300" title={status.stripe.error}>
                  ✗ {status.stripe.error.slice(0, 60)}
                </span>
              ) : status.stripe.price_active === true ? (
                <>
                  <span className="text-emerald-300">activo</span>
                  {formattedPrice && (
                    <span className="text-zinc-400">· {formattedPrice} / {status.stripe.interval ?? "month"}</span>
                  )}
                </>
              ) : status.stripe.price_active === false ? (
                <span className="text-amber-300">archivado</span>
              ) : (
                <span className="text-zinc-500">—</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-zinc-400">Logo:</span>
              {status.logo.env_value == null ? (
                <span className="text-amber-300">env no set</span>
              ) : status.logo.in_sync === true ? (
                <span className="text-emerald-300">sincronizado</span>
              ) : status.logo.in_sync === false ? (
                <span className="text-amber-300">desincronizado — se actualiza en el próximo checkout</span>
              ) : (
                <span className="text-zinc-500">—</span>
              )}
            </div>
          </div>
          {status.hints.length > 0 && (
            <ul className="mt-1 list-disc space-y-0.5 pl-3.5 text-[10px] text-zinc-400">
              {status.hints.slice(0, 4).map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          )}
          <div className="pt-1">
            <Link
              to="/admin/suscripciones"
              className="inline-flex h-5 items-center gap-1 rounded border border-violet-500/30 bg-violet-500/10 px-1.5 text-[10px] text-violet-100 transition hover:bg-violet-500/20"
            >
              Configurar Price ID
              <ArrowUpRight className="h-2.5 w-2.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryChip({
  icon: Icon,
  label,
  to,
}: {
  icon: typeof Sparkles;
  label: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="inline-flex h-6 items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2 text-[10.5px] text-zinc-300 transition hover:bg-white/10 hover:text-white"
    >
      <Icon className="h-3 w-3" />
      {label}
    </Link>
  );
}
